import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import RedlineChangeSchema, {
  RedlineChangeJSON,
} from "work/proposals/redlining/changes/_schemas/redline-change-schema";
import { z } from "zod";
import { RedlineChange } from "../../changes/redline-change";
import FeeScheduleCategoryRedlineSchema from "./fee-schedule-category-redline-schema";

export type FeeScheduleRedlineJSON = z.infer<typeof FeeScheduleRedlineSchema.FeeScheduleRedlineJSONZ>;
export type FeeScheduleRedlineParams = z.infer<typeof FeeScheduleRedlineSchema.FeeScheduleRedlineZ>;

export default class FeeScheduleRedlineSchema {
  public static readonly FeeScheduleRedlineJSONZ = z.object({
    sessionHistory: z.array(z.custom<RedlineChangeJSON>()),
    lastEntryUpdated: z.uuid().nullish(),
    redlines: z.array(FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineJSONZ),
  });
  public static readonly FeeScheduleRedlineZ = z.object({
    sessionHistory: z.array(z.custom<RedlineChange>()),
    lastEntryUpdated: z.instanceof(Guid).optional(),
    redlines: z.array(FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineZ),
  });

  public static validate(values: unknown): FeeScheduleRedlineParams {
    return parseOrThrow(FeeScheduleRedlineSchema.FeeScheduleRedlineZ, values, {
      logError: true,
      schemaName: "FeeScheduleRedline",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): FeeScheduleRedlineJSON {
    return parseOrThrow(FeeScheduleRedlineSchema.FeeScheduleRedlineJSONZ, values, {
      logError: true,
      schemaName: "FeeScheduleRedline",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is FeeScheduleRedlineParams {
    try {
      FeeScheduleRedlineSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is FeeScheduleRedlineJSON {
    try {
      FeeScheduleRedlineSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: FeeScheduleRedlineJSON): FeeScheduleRedlineParams {
    const parsed = parseOrThrow(FeeScheduleRedlineSchema.FeeScheduleRedlineJSONZ, data, {
      logError: true,
      schemaName: "FeeScheduleRedline",
      operation: "deserialize",
      payloadSample: data,
    });
    return FeeScheduleRedlineSchema.codec.decode(parsed);
  }

  public static serialize(params: FeeScheduleRedlineParams): FeeScheduleRedlineJSON {
    const parsed = FeeScheduleRedlineSchema.validate(params);
    return FeeScheduleRedlineSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleRedlineSchema.FeeScheduleRedlineJSONZ,
    FeeScheduleRedlineSchema.FeeScheduleRedlineZ,
    {
      decode: (data) => ({
        sessionHistory: data.sessionHistory.map((change) => new RedlineChange(RedlineChangeSchema.deserialize(change))),
        lastEntryUpdated: Guid.fromJSON(data.lastEntryUpdated) ?? undefined,
        redlines: data.redlines.map(FeeScheduleCategoryRedlineSchema.deserialize),
      }),
      encode: (value) => ({
        sessionHistory: value.sessionHistory.map(RedlineChangeSchema.serialize),
        lastEntryUpdated: value.lastEntryUpdated?.toJSON(),
        redlines: value.redlines.map(FeeScheduleCategoryRedlineSchema.serialize),
      }),
    }
  );
}
